import { OPCIONES, OPCIONES_POR_CLAVE } from './catalogo';
import { obj, str } from './tipos';

/**
 * Cómo se lee una respuesta cualquiera de la ficha de Yichang, conocida o no.
 *
 * 🔴 **Existe para que un campo nuevo del formulario no desaparezca de la bandeja.** La ficha se
 * guarda entera, sin lista cerrada (a diferencia de `bod-form`, que descartaba en silencio lo que no
 * conocía), así que la bandeja tiene que poder mostrar una llave que hoy no existe. Si el catálogo
 * no la conoce, el rótulo se arma con el nombre de la llave y el número de pregunta que la llave ya
 * trae adentro (`t4b_1_…` → «T4b.1»): se ve peor que una pregunta con su texto, pero se ve.
 */

/**
 * El número de pregunta que el tradicional mete en la llave: `t4b_1_pringles_chica` → `T4b.1`,
 * `m4_1_estado_por_marca` → `m4.1`, `e6_nombre_bodega` → `E.6`, `me2_mercado` → `mE.2`,
 * `j1_afluencia` → `J.1`. El canal moderno no lo hace (sus llaves son de bloque, no de pregunta),
 * y ahí da `null`.
 */
export function codigoDeClave(k: string): string | null {
  let m = /^t(\d+[a-z]?)_(\d+)_/.exec(k);
  if (m) return `T${m[1]}.${m[2]}`;
  m = /^m(\d+)_(\d+)_/.exec(k);
  if (m) return `m${m[1]}.${m[2]}`;
  m = /^me(\d+)_/.exec(k);
  if (m) return `mE.${m[1]}`;
  m = /^e(\d+)_/.exec(k);
  if (m) return `E.${m[1]}`;
  m = /^j(\d+)_/.exec(k);
  if (m) return `J.${m[1]}`;
  return null;
}

/**
 * Un rótulo legible armado del nombre de la llave: sin el prefijo de código, con espacios y con
 * mayúscula inicial. `m6_otra_competencia_texto` → «Otra competencia texto».
 */
export function humanizarClave(k: string): string {
  const sin = k
    .replace(/^t\d+[a-z]?_\d+_/, '')
    .replace(/^(me|m|e|j|c)\d+_(\d+_)?/, '')
    .replace(/_/g, ' ')
    .trim();
  const base = sin === '' ? k.replace(/_/g, ' ') : sin;
  return base.charAt(0).toUpperCase() + base.slice(1);
}

/**
 * Cómo se lee un valor guardado como identificador (`la_saco`, `no_vende`…). Primero el mapa de la
 * pregunta, después el general, y si ninguno lo conoce, el identificador con espacios: nunca se
 * esconde un valor por no conocerlo. Un texto que ya es legible («Lata chica (19-21 g)») pasa tal
 * cual.
 */
export function etiquetaOpcion(v: string, clave?: string): string {
  const porClave = clave !== undefined ? OPCIONES_POR_CLAVE[clave] : undefined;
  if (porClave?.[v] !== undefined) return porClave[v];
  if (OPCIONES[v] !== undefined) return OPCIONES[v];
  if (/^[a-z0-9]+(_[a-z0-9]+)+$/.test(v)) {
    const t = v.replace(/_/g, ' ');
    return t.charAt(0).toUpperCase() + t.slice(1);
  }
  return v;
}

/** Una ruta del bucket de fotos del formulario (o la de práctica del modo demo). */
export const esRutaFoto = (v: unknown): v is string =>
  typeof v === 'string' && /^(yichang-form|demo)\//.test(v);

/**
 * Si un valor es una foto, cuál: `{ ruta }` si subió, `{ fallida }` si se tomó y no subió.
 *
 * Las dos formas existen porque los dos formularios guardan distinto: el moderno deja la RUTA suelta
 * (su `fichaLimpia` reemplaza cada foto por su ruta y compacta las que no subieron) y el tradicional
 * deja el objeto `{ esFoto, nombre, ruta, error }` (su `sinBinarios` solo le saca el archivo).
 */
export function comoFoto(v: unknown): { ruta: string } | { fallida: string | null } | null {
  if (esRutaFoto(v)) return { ruta: v };
  const o = obj(v);
  if (o === null) return null;
  if (o.esFoto !== true && o.tipo !== 'foto') return null;
  const ruta = str(o.ruta);
  if (ruta !== null) return { ruta };
  return { fallida: str(o.error) };
}

/** ¿Es una lista de fotos? Una lista vacía no lo es: no se sabe de qué era. */
export const esListaDeFotos = (v: unknown): boolean =>
  Array.isArray(v) && v.length > 0 && v.every((x) => comoFoto(x) !== null);

/**
 * ¿La respuesta está vacía? `''`, `null`, `[]` y `{}` son «sin respuesta». **`0` y `false` no**:
 * cero cajas con Pringles es un hallazgo, y «no» es una respuesta.
 */
export function vacia(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  const o = obj(v);
  if (o !== null) return Object.values(o).every(vacia);
  return false;
}
